// useConceptMap — Concept map state management + Firestore persistence
//
// Processes show_concept_map tool results from useAnnaChat,
// accumulates nodes/edges, applies deterministic didactic layouts,
// and debounce-saves to Firestore via topicNoteService.

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '@services/firebase/config';
import { getTopicNote } from '@services/firebase/topicNoteService';
import { getLayout } from '../layouts';
import type { CanvasSize } from '../layouts/types';
import type {
  ModelStructure,
  DidacticModel,
  NodeVisualState,
  TopicDidacticConfig,
} from '../types/conceptMap';
import type { ToolResult } from './useAnnaChat';
import type { BloomLevel, ConceptMapData, RelationType, ConceptMapMeta } from '../types';
import type { ConceptNode, ConceptEdge } from '../types/conceptMap';

interface UseConceptMapOptions {
  userId: string;
  topicId: string;
  journeyId: string;
  topicTitle: string;
  didacticConfig?: TopicDidacticConfig;
}

interface UseConceptMapReturn {
  nodes: Node[];
  edges: Edge[];
  highlightedNodeId: string | null;
  isLoading: boolean;
  meta: ConceptMapMeta | null;
  processToolResult: (tr: ToolResult) => void;
  onNodeDragStop: (nodeId: string, position: { x: number; y: number }) => void;
  updateMeta: (updates: Partial<ConceptMapMeta>) => void;
}

const DEFAULT_CANVAS_SIZE: CanvasSize = { width: 800, height: 600 };

/**
 * Compute deterministic layout positions based on didactic model type.
 * Same input always produces same output — no simulation.
 */
function computeDidacticLayout(
  modelStructure: ModelStructure,
  didacticModel: DidacticModel,
  canvasSize: CanvasSize = DEFAULT_CANVAS_SIZE
): Map<string, { x: number; y: number }> {
  const layout = getLayout(didacticModel);
  const positioned = layout(modelStructure.nodes, modelStructure.edges, canvasSize);

  const positions = new Map<string, { x: number; y: number }>();
  for (const node of positioned) {
    positions.set(node.id, { x: node.x, y: node.y });
  }
  return positions;
}

function toReactFlowNodes(
  nodes: ConceptNode[],
  depthMap: Map<string, number>
): Node[] {
  return nodes.map((node) => ({
    id: node.id,
    type: 'conceptMapNode',
    position: node.position ?? { x: 0, y: 0 },
    data: {
      label: node.label,
      bloomLevel: node.bloomLevel ?? 'recognize',
      depth: node.depth ?? depthMap.get(node.id) ?? 0,
      isHighlighted: false,
      visualState: node.visualState ?? 'active',
      role: node.role,
    },
    draggable: node.visualState !== 'dormant',
  }));
}

function toReactFlowEdges(edges: ConceptEdge[]): Edge[] {
  return edges.map((e) => {
    const base = {
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      type: 'default',
      labelStyle: { fill: '#9ca3af', fontSize: 10 },
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: '#111827', fillOpacity: 0.9 },
    };

    switch (e.relationType) {
      case 'causal':
        return {
          ...base,
          markerEnd: { type: 'arrowclosed' as const, color: '#60a5fa' },
          style: { stroke: '#60a5fa', strokeWidth: 2, opacity: 0.4 },
        };
      case 'interdependent':
        return {
          ...base,
          markerStart: { type: 'arrowclosed' as const, color: '#a78bfa' },
          markerEnd: { type: 'arrowclosed' as const, color: '#a78bfa' },
          style: { stroke: '#a78bfa', strokeWidth: 1.5, opacity: 0.4 },
        };
      case 'related':
      default:
        return {
          ...base,
          style: { stroke: '#4b5563', strokeWidth: 1.5, opacity: 0.4 },
        };
    }
  });
}

function serializeForFirestore(
  nodes: ConceptNode[],
  edges: ConceptEdge[],
  meta?: ConceptMapMeta | null,
): ConceptMapData {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      label: n.label,
      bloomLevel: n.bloomLevel,
      pinned: n.pinned,
      position: n.position,
      origin: n.origin,
      ...(n.addedInSessionId ? { addedInSessionId: n.addedInSessionId } : {}),
      ...(n.visualState ? { visualState: n.visualState } : {}),
      ...(n.role ? { role: n.role } : {}),
      ...(n.depth !== undefined ? { depth: n.depth } : {}),
    })),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      ...(e.label ? { label: e.label } : {}),
      relationType: e.relationType,
    })),
    ...(meta ? { meta } : {}),
  };
}

export function useConceptMap(options: UseConceptMapOptions): UseConceptMapReturn {
  const { userId, topicId, didacticConfig } = options;

  const [conceptNodes, setConceptNodes] = useState<ConceptNode[]>([]);
  const [conceptEdges, setConceptEdges] = useState<ConceptEdge[]>([]);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [meta, setMeta] = useState<ConceptMapMeta | null>(null);
  // depthMap kept for legacy add_nodes path; depth now primarily comes from node.depth
  const [depthMap] = useState<Map<string, number>>(new Map());

  // Refs for debounced save
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nodesRef = useRef(conceptNodes);
  const edgesRef = useRef(conceptEdges);
  const metaRef = useRef(meta);
  nodesRef.current = conceptNodes;
  edgesRef.current = conceptEdges;
  metaRef.current = meta;

  // Load from Firestore on mount
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const result = await getTopicNote(userId, topicId);
      if (cancelled) return;

      if (result.success && result.data?.conceptMap) {
        const cm = result.data.conceptMap;
        const loadedNodes: ConceptNode[] = cm.nodes.map(n => ({
          ...n,
          origin: n.origin || 'introduction',
        }));
        const loadedEdges: ConceptEdge[] = cm.edges.map(e => ({
          ...e,
          relationType: (e.relationType || 'related') as RelationType,
        }));

        if (didacticConfig?.modelStructure) {
          const positions = computeDidacticLayout(
            didacticConfig.modelStructure,
            didacticConfig.didacticModel
          );

          if (loadedNodes.length === 0) {
            // Initialize from modelStructure — all nodes start as dormant
            const initialNodes: ConceptNode[] = didacticConfig.modelStructure.nodes.map((mn) => ({
              id: mn.id,
              label: mn.label,
              role: mn.role,
              depth: mn.depth,
              bloomLevel: 'recognize' as BloomLevel,
              pinned: false,
              visualState: 'dormant' as NodeVisualState,
              position: positions.get(mn.id) ?? { x: 0, y: 0 },
              origin: 'introduction' as const,
            }));
            const initialEdges: ConceptEdge[] = didacticConfig.modelStructure.edges.map((me) => ({
              id: `${me.source}-${me.target}`,
              source: me.source,
              target: me.target,
              label: me.relation,
              relationType: 'related' as RelationType,
            }));
            setConceptNodes(initialNodes);
            setConceptEdges(initialEdges);
          } else {
            // Existing nodes: apply layout positions but keep their visualState
            const repositioned = loadedNodes.map((n) => ({
              ...n,
              position: positions.get(n.id) ?? n.position,
            }));
            setConceptNodes(repositioned);
            setConceptEdges(loadedEdges);
          }
        } else {
          // Legacy path: no didactic config, just use loaded data as-is
          setConceptNodes(loadedNodes);
          setConceptEdges(loadedEdges);
        }

        if (cm.meta) {
          setMeta(cm.meta);
        }
      } else if (didacticConfig?.modelStructure) {
        // No persisted data at all — initialize from modelStructure
        const positions = computeDidacticLayout(
          didacticConfig.modelStructure,
          didacticConfig.didacticModel
        );
        const initialNodes: ConceptNode[] = didacticConfig.modelStructure.nodes.map((mn) => ({
          id: mn.id,
          label: mn.label,
          role: mn.role,
          depth: mn.depth,
          bloomLevel: 'recognize' as BloomLevel,
          pinned: false,
          visualState: 'dormant' as NodeVisualState,
          position: positions.get(mn.id) ?? { x: 0, y: 0 },
          origin: 'introduction' as const,
        }));
        const initialEdges: ConceptEdge[] = didacticConfig.modelStructure.edges.map((me) => ({
          id: `${me.source}-${me.target}`,
          source: me.source,
          target: me.target,
          label: me.relation,
          relationType: 'related' as RelationType,
        }));
        setConceptNodes(initialNodes);
        setConceptEdges(initialEdges);
      }

      setIsLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [userId, topicId, didacticConfig]);

  // Debounced save to Firestore
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const data = serializeForFirestore(nodesRef.current, edgesRef.current, metaRef.current);
      // Use setDoc with merge to write ONLY conceptMap — avoids overwriting
      // user-written content, sessionKeywords, or examConcepts on the TopicNote.
      const docId = `${userId}_${topicId}`;
      const docRef = doc(db, 'topicNotes', docId);
      setDoc(docRef, { conceptMap: data, userId, topicId }, { merge: true });
    }, 2000);
  }, [userId, topicId]); // Only userId + topicId used in the callback body

  // Cleanup save timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Process a show_concept_map tool result
  const processToolResult = useCallback((tr: ToolResult) => {
    if (tr.toolName !== 'show_concept_map') return;

    const input = tr.input as {
      action?: string;
      nodes?: Array<{ id: string; label: string; bloomLevel?: BloomLevel }>;
      edges?: Array<{ source: string; target: string; label?: string; relationType?: string }>;
      highlight?: string;
      nodeIds?: string[];
      _origin?: string;
    };

    if (input.action === 'add_nodes') {
      // Legacy path: no didacticConfig. New nodes are placed at canvas center.
      // Force layout is removed; positions are static until user drags.
      const prevNodes = nodesRef.current;
      const prevEdges = edgesRef.current;

      const existingNodeIds = new Set(prevNodes.map((n) => n.id));
      const origin = (input._origin || 'introduction') as 'introduction' | 'conversation';
      const newNodes: ConceptNode[] = (input.nodes || [])
        .filter((n) => !existingNodeIds.has(n.id))
        .map((n) => ({
          id: n.id,
          label: n.label,
          bloomLevel: n.bloomLevel || 'recognize',
          pinned: false,
          position: { x: DEFAULT_CANVAS_SIZE.width / 2, y: DEFAULT_CANVAS_SIZE.height / 2 },
          origin,
          visualState: 'active' as NodeVisualState,
        }));
      const allNodes = [...prevNodes, ...newNodes];

      const newEdges = (input.edges || []).map((e) => ({
        id: `${e.source}-${e.target}`,
        source: e.source,
        target: e.target,
        label: e.label,
        relationType: (e.relationType || 'related') as RelationType,
      }));
      const existingEdgeIds = new Set(prevEdges.map((e) => e.id));
      const uniqueNewEdges = newEdges.filter((e) => !existingEdgeIds.has(e.id));
      const allEdges = [...prevEdges, ...uniqueNewEdges];

      setConceptNodes(allNodes);
      setConceptEdges(allEdges);
      scheduleSave();
    } else if (input.action === 'activate_node') {
      const nodeIds: string[] = input.nodeIds ?? [];
      setConceptNodes((prev) =>
        prev.map((n) =>
          nodeIds.includes(n.id) ? { ...n, visualState: 'active' as NodeVisualState } : n
        )
      );
      scheduleSave();
    } else if (input.action === 'highlight') {
      const highlightIds: string[] = input.highlight
        ? [input.highlight]
        : (input.nodeIds ?? []);
      setConceptNodes((prev) =>
        prev.map((n) => ({
          ...n,
          visualState: highlightIds.includes(n.id)
            ? 'highlighted' as NodeVisualState
            : n.visualState === 'highlighted'
              ? 'active' as NodeVisualState
              : n.visualState,
        }))
      );
      // Keep highlightedNodeId in sync for backward compat with consumers
      setHighlightedNodeId(input.highlight || null);
      // Highlight is ephemeral (UI-only), not persisted to Firestore
    } else if (input.action === 'update_bloom') {
      setConceptNodes((prev) =>
        prev.map((node) => {
          const update = (input.nodes || []).find((n) => n.id === node.id);
          if (update?.bloomLevel) {
            return { ...node, bloomLevel: update.bloomLevel };
          }
          return node;
        }),
      );
      scheduleSave();
    }
  }, [scheduleSave]);

  // Update concept map meta and schedule a save
  const updateMeta = useCallback((updates: Partial<ConceptMapMeta>) => {
    setMeta(prev => {
      const now = new Date().toISOString();
      const base: ConceptMapMeta = prev ?? {
        topicId,
        createdAt: now,
        lastModifiedAt: now,
        lastModifiedIn: 'introduction',
        totalSessions: 0,
      };
      return { ...base, ...updates, lastModifiedAt: now };
    });
    scheduleSave();
  }, [topicId, scheduleSave]);

  // Handle node drag (marks node as pinned)
  const onNodeDragStop = useCallback(
    (nodeId: string, position: { x: number; y: number }) => {
      setConceptNodes((prev) =>
        prev.map((n) =>
          n.id === nodeId ? { ...n, pinned: true, position } : n,
        ),
      );
      scheduleSave();
    },
    [scheduleSave],
  );

  return {
    nodes: toReactFlowNodes(conceptNodes, depthMap),
    edges: toReactFlowEdges(conceptEdges),
    highlightedNodeId,
    isLoading,
    meta,
    processToolResult,
    onNodeDragStop,
    updateMeta,
  };
}
